import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Plus, Pencil, Trash2, X, ArrowLeft, Store, Phone, MapPin, Package, Search, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const CATEGORIES = ["flowers", "cakes", "decorations", "hampers", "packages"];
const PRODUCT_LABELS = ["Coming Soon", "Inspiration Only"];

export default function VendorsAdmin() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (activeFilter !== "") params.set("active", activeFilter);
    api.get(`/admin/vendors${params.toString() ? `?${params}` : ""}`).then((r) => setItems(r.data));
  }, [q, activeFilter]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!confirm("Delete this vendor? Their products will be detached (not deleted).")) return;
    await api.delete(`/admin/vendors/${id}`);
    toast.success("Vendor deleted");
    load();
  };

  const toggleActive = async (v) => {
    await api.patch(`/admin/vendors/${v.id}`, { ...v, active: !v.active });
    load();
  };

  if (selectedVendor) {
    return <VendorDetail vendor={selectedVendor} onBack={() => { setSelectedVendor(null); load(); }} />;
  }

  return (
    <div className="space-y-5" data-testid="vendors-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search className="w-4 h-4 text-charcoal/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search shop, owner, area…"
              data-testid="vendor-search"
              className="w-64 rounded-full bg-white border border-warm pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-rose-400" />
          </div>
          <button onClick={() => setActiveFilter("")} data-testid="vf-all"
            className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${activeFilter === "" ? "bg-charcoal text-white border-charcoal" : "bg-white text-charcoal border-warm"}`}>All</button>
          <button onClick={() => setActiveFilter("true")} data-testid="vf-active"
            className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${activeFilter === "true" ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-charcoal border-warm"}`}>Active</button>
          <button onClick={() => setActiveFilter("false")} data-testid="vf-inactive"
            className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${activeFilter === "false" ? "bg-rose-500 text-white border-rose-500" : "bg-white text-charcoal border-warm"}`}>Inactive</button>
        </div>
        <button onClick={() => { setEditing(null); setShowDialog(true); }} data-testid="add-vendor-btn"
          className="rounded-full px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-rose-500/40 flex items-center gap-2"
          style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}>
          <Plus className="w-4 h-4" strokeWidth={2.25} /> Add vendor
        </button>
      </div>

      {items.length === 0 ? (
        <div className="card-surface p-10 text-center" data-testid="vendors-empty">
          <Store className="w-8 h-8 text-rose-500 mx-auto mb-3" strokeWidth={1.5} />
          <p className="font-display text-2xl text-charcoal">No vendors yet</p>
          <p className="text-charcoal/60 mt-2">Add your first Jaipur partner to get started.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((v, i) => (
            <motion.div key={v.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              className="card-surface p-5" data-testid={`vendor-card-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-display text-lg text-charcoal truncate">{v.shop_name}</p>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${v.active ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                      {v.active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <p className="text-xs text-charcoal/60 mt-0.5">{v.owner_name}</p>
                  <p className="text-xs text-charcoal/60 mt-2 flex items-center gap-1"><MapPin className="w-3 h-3" strokeWidth={1.75} />{v.area} · {v.pincode}</p>
                  <p className="text-xs text-charcoal/60 flex items-center gap-1"><Phone className="w-3 h-3" strokeWidth={1.75} />{v.phone}</p>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {(v.categories || []).map((c) => (
                      <span key={c} className="rounded-full bg-linen-2 text-charcoal/70 border border-warm px-2 py-0.5 text-[10px] font-semibold capitalize">{c}</span>
                    ))}
                  </div>
                </div>
                <button onClick={() => toggleActive(v)} data-testid={`toggle-active-${i}`} className="p-1">
                  {v.active ? <ToggleRight className="w-6 h-6 text-emerald-500" /> : <ToggleLeft className="w-6 h-6 text-charcoal/30" />}
                </button>
              </div>
              <div className="mt-4 pt-3 border-t border-warm flex items-center justify-between">
                <span className="text-xs font-semibold text-charcoal/70 flex items-center gap-1">
                  <Package className="w-3.5 h-3.5" strokeWidth={2} /> {v.product_count} product{v.product_count === 1 ? "" : "s"}
                </span>
                <div className="flex gap-1">
                  <button onClick={() => setSelectedVendor(v)} data-testid={`view-vendor-${i}`}
                    className="rounded-full px-3 py-1 text-xs font-semibold bg-charcoal text-white hover:bg-charcoal/80">
                    View products
                  </button>
                  <button onClick={() => { setEditing(v); setShowDialog(true); }} data-testid={`edit-vendor-${i}`}
                    className="p-1.5 rounded-full hover:bg-linen-2"><Pencil className="w-3.5 h-3.5 text-charcoal/60" strokeWidth={1.75} /></button>
                  <button onClick={() => remove(v.id)} data-testid={`del-vendor-${i}`}
                    className="p-1.5 rounded-full hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5 text-rose-500" strokeWidth={1.75} /></button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {showDialog && (
        <VendorDialog initial={editing} onClose={() => setShowDialog(false)}
          onSaved={() => { setShowDialog(false); load(); }} />
      )}
    </div>
  );
}

function VendorDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    shop_name: initial?.shop_name || "",
    owner_name: initial?.owner_name || "",
    phone: initial?.phone || "",
    area: initial?.area || "",
    pincode: initial?.pincode || "",
    categories: initial?.categories || [],
    active: initial?.active !== false,
    notes: initial?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const toggleCat = (c) => {
    setForm((f) => ({ ...f, categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.categories.length === 0) { toast.error("Select at least one category"); return; }
    setSaving(true);
    try {
      if (initial) await api.patch(`/admin/vendors/${initial.id}`, form);
      else await api.post("/admin/vendors", form);
      toast.success(initial ? "Vendor updated" : "Vendor added");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/50 backdrop-blur-sm" onClick={onClose} data-testid="vendor-dialog">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <header className="p-6 border-b border-warm flex items-center justify-between sticky top-0 bg-white z-10">
          <p className="font-display text-2xl text-charcoal">{initial ? "Edit vendor" : "Add vendor"}</p>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-linen-2"><X className="w-5 h-5" /></button>
        </header>
        <div className="p-6 grid sm:grid-cols-2 gap-4">
          <F label="Shop name *"><Inp value={form.shop_name} onChange={(v) => setForm({ ...form, shop_name: v })} required testId="vf-shop-name" /></F>
          <F label="Owner name *"><Inp value={form.owner_name} onChange={(v) => setForm({ ...form, owner_name: v })} required testId="vf-owner-name" /></F>
          <F label="Phone *"><Inp type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required testId="vf-phone" /></F>
          <F label="Area *"><Inp value={form.area} onChange={(v) => setForm({ ...form, area: v })} required testId="vf-area" placeholder="C-Scheme, Malviya Nagar…" /></F>
          <F label="Pincode *"><Inp value={form.pincode} onChange={(v) => setForm({ ...form, pincode: v })} required testId="vf-pincode" placeholder="302001" /></F>
          <label className="flex items-center gap-2 mt-6 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
              className="w-4 h-4 rounded accent-rose-500" data-testid="vf-active" />
            <span className="text-sm text-charcoal/80">Active (accepting orders)</span>
          </label>
          <div className="sm:col-span-2">
            <F label="Product categories *">
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => {
                  const on = form.categories.includes(c);
                  return (
                    <button key={c} type="button" onClick={() => toggleCat(c)}
                      data-testid={`vf-cat-${c}`}
                      className={`rounded-full px-4 py-1.5 text-xs font-semibold border capitalize transition ${on ? "bg-charcoal text-white border-charcoal" : "bg-white text-charcoal border-warm hover:border-charcoal"}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            </F>
          </div>
          <div className="sm:col-span-2">
            <F label="Notes (optional)">
              <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full rounded-xl bg-white border border-warm px-4 py-3 resize-none focus:outline-none focus:border-rose-400"
                data-testid="vf-notes" placeholder="Internal notes about this vendor…" />
            </F>
          </div>
        </div>
        <footer className="p-6 border-t border-warm flex justify-end gap-3 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="pill-btn pill-btn-secondary text-sm" data-testid="vf-cancel">Cancel</button>
          <button type="submit" disabled={saving} data-testid="vf-save"
            className="rounded-full px-6 py-3 text-sm font-bold text-white shadow-lg shadow-rose-500/40 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}>
            {saving ? "Saving…" : "Save vendor"}
          </button>
        </footer>
      </form>
    </div>
  );
}

/* Vendor drill-down: shows products under a specific vendor */
function VendorDetail({ vendor, onBack }) {
  const [products, setProducts] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/vendors/${vendor.id}/products`).then((r) => setProducts(r.data));
  }, [vendor.id]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-5" data-testid="vendor-detail">
      <button onClick={onBack} data-testid="back-to-vendors"
        className="flex items-center gap-2 text-sm text-charcoal/60 hover:text-charcoal">
        <ArrowLeft className="w-4 h-4" /> Back to vendors
      </button>

      <div className="card-surface p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display text-2xl sm:text-3xl text-charcoal">{vendor.shop_name}</h2>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${vendor.active ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                {vendor.active ? "Active" : "Inactive"}
              </span>
            </div>
            <p className="text-sm text-charcoal/70 mt-1">{vendor.owner_name} · {vendor.phone}</p>
            <p className="text-sm text-charcoal/60 mt-1">{vendor.area} · {vendor.pincode}</p>
          </div>
          <button onClick={() => { setEditing(null); setShowDialog(true); }} data-testid="vendor-add-product"
            className="rounded-full px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-rose-500/40 flex items-center gap-2"
            style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}>
            <Plus className="w-4 h-4" strokeWidth={2.25} /> Add product
          </button>
        </div>
      </div>

      {products.length === 0 ? (
        <div className="card-surface p-10 text-center" data-testid="vendor-products-empty">
          <Package className="w-8 h-8 text-rose-500 mx-auto mb-3" strokeWidth={1.5} />
          <p className="font-display text-2xl text-charcoal">No products yet</p>
          <p className="text-charcoal/60 mt-2">Add products this vendor prepares.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p, i) => (
            <div key={p.id} className="card-surface overflow-hidden" data-testid={`vendor-product-${i}`}>
              <div className="relative">
                <img src={p.image_url} className="w-full aspect-video object-cover" alt="" />
                {p.label && (
                  <span className="absolute top-2 left-2 rounded-full bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-widest">
                    {p.label}
                  </span>
                )}
                {!p.available && (
                  <span className="absolute top-2 right-2 rounded-full bg-charcoal/80 text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-widest">
                    Unavailable
                  </span>
                )}
              </div>
              <div className="p-4">
                <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-rose-500 capitalize">{p.category}</p>
                <p className="font-display text-lg text-charcoal mt-1">{p.name}</p>
                <p className="text-sm text-charcoal/60 line-clamp-2 mt-1">{p.description}</p>
                {p.delivery_pincode && (
                  <p className="text-xs text-charcoal/60 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{p.delivery_pincode}</p>
                )}
                <div className="flex items-center justify-between mt-3">
                  <span className="font-display text-xl text-charcoal">₹{p.price?.toLocaleString('en-IN')}</span>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditing(p); setShowDialog(true); }} className="p-2 rounded-full hover:bg-linen-2" data-testid={`vp-edit-${i}`}>
                      <Pencil className="w-4 h-4 text-charcoal/60" strokeWidth={1.75} />
                    </button>
                    <button onClick={() => remove(p.id)} className="p-2 rounded-full hover:bg-rose-50" data-testid={`vp-del-${i}`}>
                      <Trash2 className="w-4 h-4 text-rose-500" strokeWidth={1.75} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showDialog && (
        <VendorProductDialog vendor={vendor} initial={editing} onClose={() => setShowDialog(false)}
          onSaved={() => { setShowDialog(false); load(); }} />
      )}
    </div>
  );
}

/* Product dialog embedded in vendor context (auto-sets vendor_id, adds pincode/label fields) */
export function VendorProductDialog({ vendor, initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    category: initial?.category || (vendor?.categories?.[0] || "flowers"),
    description: initial?.description || "",
    price: initial?.price || 999,
    image_url: initial?.image_url || "",
    available: initial?.available !== false,
    tags: (initial?.tags || []).join(", "),
    vendor_id: vendor?.id || initial?.vendor_id || null,
    delivery_pincode: initial?.delivery_pincode || vendor?.pincode || "",
    label: initial?.label || "",
    is_global: !!initial?.is_global,
  });
  const [saving, setSaving] = useState(false);
  const cats = vendor?.categories?.length ? vendor.categories : CATEGORIES;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseInt(form.price),
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        label: form.label || null,
        delivery_pincode: form.delivery_pincode || null,
        is_global: !!form.is_global,
      };
      if (initial) await api.patch(`/products/${initial.id}`, payload);
      else await api.post("/products", payload);
      toast.success(initial ? "Product updated" : "Product added");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/50 backdrop-blur-sm" onClick={onClose} data-testid="vendor-product-dialog">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <header className="p-6 border-b border-warm flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-rose-500">{vendor?.shop_name}</p>
            <p className="font-display text-2xl text-charcoal mt-0.5">{initial ? "Edit product" : "Add product"}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-linen-2"><X className="w-5 h-5" /></button>
        </header>
        <div className="p-6 grid sm:grid-cols-2 gap-4">
          <F label="Product name *"><Inp value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="vpf-name" /></F>
          <F label="Category *">
            <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400 capitalize" data-testid="vpf-category">
              {cats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </F>
          <F label="Price (₹) *"><Inp type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} required testId="vpf-price" /></F>
          <F label="Image URL *"><Inp value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} required testId="vpf-image" placeholder="https://…" /></F>
          <F label="Delivery pincode"><Inp value={form.delivery_pincode} onChange={(v) => setForm({ ...form, delivery_pincode: v })} testId="vpf-pincode" placeholder="302001" /></F>
          <F label="Availability label (optional)">
            <select value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
              className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400" data-testid="vpf-label">
              <option value="">— Available now —</option>
              {PRODUCT_LABELS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </F>
          <div className="sm:col-span-2">
            <F label="Description *">
              <textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full rounded-xl bg-white border border-warm px-4 py-3 resize-none focus:outline-none focus:border-rose-400" data-testid="vpf-description" />
            </F>
          </div>
          <div className="sm:col-span-2">
            <F label="Tags (comma separated)"><Inp value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} testId="vpf-tags" /></F>
          </div>
          <label className="flex items-center gap-2 sm:col-span-2 cursor-pointer">
            <input type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })}
              className="w-4 h-4 rounded accent-rose-500" data-testid="vpf-available" />
            <span className="text-sm text-charcoal/80">Available for booking</span>
          </label>
          <label className="flex items-center gap-2 sm:col-span-2 cursor-pointer" title="Global products always ship free — no pincode-based delivery fee.">
            <input type="checkbox" checked={form.is_global} onChange={(e) => setForm({ ...form, is_global: e.target.checked })}
              className="w-4 h-4 rounded accent-rose-500" data-testid="vpf-is-global" />
            <span className="text-sm text-charcoal/80">Global product (always free delivery)</span>
          </label>
        </div>
        <footer className="p-6 border-t border-warm flex justify-end gap-3 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="pill-btn pill-btn-secondary text-sm" data-testid="vpf-cancel">Cancel</button>
          <button type="submit" disabled={saving} data-testid="vpf-save"
            className="rounded-full px-6 py-3 text-sm font-bold text-white shadow-lg shadow-rose-500/40 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}>
            {saving ? "Saving…" : "Save"}
          </button>
        </footer>
      </form>
    </div>
  );
}

const F = ({ label, children }) => <div><label className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2 block">{label}</label>{children}</div>;
const Inp = ({ value, onChange, type = "text", required, testId, placeholder }) => (
  <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
    className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400" data-testid={testId} />
);
